import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiHttp } from '@core/api/api-http';

import { LibraryType } from '../models/library-type';
import {
  NavigationRecord,
  LibraryFolder,
  LibraryItem,
  SearchResults
} from '../models/navigation-record';
import { StripTagsPipe } from '../pipes/strip-tags.pipe';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class LibraryService {
  private stripTagsPipe = new StripTagsPipe();

  constructor(private http: ApiHttp) {}

  getRootFolders(libraryType: LibraryType): Observable<LibraryFolder[]> {
    return this.http.get<LibraryFolder[]>(environment.libraryApiUrl, `library/${libraryType}/records`);
  }

  getNavigationRecords(
    libraryType: LibraryType,
    folderId: string
  ): Observable<NavigationRecord> {
    return this.http.get<NavigationRecord>(
      environment.libraryApiUrl,
      `library/${libraryType}/records/${folderId}`
    );
  }

  getRecentItems(
    libraryType: LibraryType,
    count: number = 10
  ): Observable<LibraryItem[]> {
    return this.http.get<LibraryItem[]>(environment.libraryApiUrl, `library/${libraryType}/recent`);
  }

  updateRecentItems(libraryType: LibraryType, item: LibraryItem) {
    return this.http.post(
      environment.libraryApiUrl, 
      `library/${libraryType}/recent`, 
      { id: item.id,
        filename: item.filename,
        url: item.url.replace('/', '\\/')});
  }

  getBookmarkedItems(libraryType: LibraryType): Observable<LibraryItem[]> {
    return this.http.get<LibraryItem[]>(environment.libraryApiUrl, `library/${libraryType}/bookmarks`);
  }

  getPinnedFolders(libraryType: LibraryType): Observable<LibraryItem[]> {
    return this.http.get<LibraryItem[]>(environment.libraryApiUrl, `library/${libraryType}/pinned`);
  }

  // TODO: remove parameter (foldername) [future release / dep. API change]
  pinFolder(libraryType: LibraryType, folderId: string, foldername: string) {
    return this.http.post(
      environment.libraryApiUrl, 
      `library/${libraryType}/pinned`, 
      { id: folderId,
        foldername: foldername });
  }

  unpinFolder(libraryType: LibraryType, folderId: string) {
    return this.http.delete(environment.libraryApiUrl, `library/${libraryType}/pinned/${folderId}`);
  }

  // TODO: remove parameters (url, filename) [future release / dep. API change]
  // notes: stripTags pipe used due to WAF vs filename html (from search)
  bookmarkItem(
    libraryType: LibraryType,
    itemId: string,
    url: string,
    itemName: string
  ) {
    return this.http.post(
      environment.libraryApiUrl,
      `library/${libraryType}/bookmarks`, 
      { id: itemId,
        url: url.replace('/', '\\/'),
        filename: this.stripTagsPipe.transform(itemName) });
  }

  unbookmarkItem(libraryType: LibraryType, itemId: string) {
    return this.http.delete(environment.libraryApiUrl, `library/${libraryType}/bookmarks/${itemId}`);
  }

  getAnnouncements(): Observable<NavigationRecord> {
    return this.http.get<NavigationRecord>(environment.libraryApiUrl, `library/announcements`);
  }

  search(
    libraryType: LibraryType,
    searchTerm: string
  ): Observable<SearchResults> {
    return this.http.post(
      environment.libraryApiUrl,
      `library/${libraryType}/search`, 
      { searchTerm: searchTerm });
  }
}
